import React from 'react';
import { Routes, Route, Navigate, Outlet, useLocation, useOutletContext } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectEffectiveModules, selectUserTypeId, USER_TYPE } from './redux/slices/authSlice';
import { canAccessPath, firstAccessibleRoute, hasModule } from './data/serverModules';
import LoginPage from './pages/LoginPage';
import DashboardLayout from './layouts/DashboardLayout';
import DashboardPage from './pages/DashboardPage';
import MasterDashboardPage from './pages/MasterDashboardPage';
import MyInfoPage from './pages/MyInfoPage';
import EmployeesPage from './pages/EmployeesPage';
import OnboardEmployeePage from './pages/OnboardEmployeePage';
import BulkImportEmployeesPage from './pages/BulkImportEmployeesPage';
import EmployeeDetailPage from './pages/EmployeeDetailPage';
import RolesPage from './pages/RolesPage';
import RoleUsersPage from './pages/RoleUsersPage';
import RoleAccessPage from './pages/RoleAccessPage';
import SettingsPage from './pages/SettingsPage';

import InboxPage from './pages/InboxPage';
import ChatPage from './pages/ChatPage';
import ChatSettingsPage from './pages/ChatSettingsPage';
import DocumentsPage from './pages/DocumentsPage';
import RecruitmentPage from './pages/RecruitmentPage';
import CandidateDetailPage from './pages/CandidateDetailPage';

import PayslipsPage from './pages/PayslipsPage';
import PayAdjustmentsPage from './pages/PayAdjustmentsPage';

import RunPayrollPage from './pages/RunPayrollPage';
import ExpensesPage from './pages/ExpensesPage';
import AttendanceLeavePage from './pages/AttendanceLeavePage';
import LeaveManagementPage from './components/LeaveAttendanceComps/LeaveManagementPage';
import AttendanceHistoryPage from './pages/AttendanceHistoryPage';
import MyActivityPage from './pages/MyActivityPage';
import MyDocumentsPage from './pages/MyDocumentsPage';import FieldOperationsPage from './pages/FieldOperationsPage';
import OrganisationPage from './pages/OrganisationPage';
import BusinessEntitiesPage from './pages/BusinessEntitiesPage';
import PayrollSetupPage from './pages/PayrollSetupPage';
import LeavePolicyPage from './pages/LeavePolicyPage';
import ResignationsPage from './pages/ResignationsPage';
import { CareersHome, CareersJob, CareersTrack } from './pages/CareersSitePage';

function ProtectedRoute() {
    const isAuthenticated = useSelector((s) => s.auth.isAuthenticated);
    return isAuthenticated ? <Outlet /> : <Navigate to="/login" replace />;
}

function PublicOnlyRoute({ children }) {
    const isAuthenticated = useSelector((s) => s.auth.isAuthenticated);
    return isAuthenticated ? <Navigate to="/dashboard" replace /> : children;
}

function ModuleGuard() {
    const ctx = useOutletContext(); // forward the layout's context (breadcrumb setters) to the page
    const modules = useSelector(selectEffectiveModules);
    const { pathname } = useLocation();

    if (canAccessPath(modules, pathname)) return <Outlet context={ctx} />;

    const fallback = firstAccessibleRoute(modules);
    if (fallback && fallback !== pathname) return <Navigate to={fallback} replace />;
    return (
        <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 24 }}>
            <div style={{ maxWidth: 420 }}>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0F172A' }}>No access</h2>
                <p style={{ marginTop: 8, fontSize: 14, color: '#64748B' }}>
                    Your account doesn&apos;t have access to any modules yet. Contact your administrator.
                </p>
            </div>
        </div>
    );
}

function DashboardHome() {
    const modules = useSelector(selectEffectiveModules);
    return hasModule(modules, 'dashboard') ? <DashboardPage /> : <MyInfoPage />;
}

function MasterAdminRoute() {
    const ctx = useOutletContext();
    const isMasterAdmin = useSelector(selectUserTypeId) === USER_TYPE.MASTER_ADMIN;
    return isMasterAdmin ? <Outlet context={ctx} /> : <Navigate to="/dashboard" replace />;
}

const MOVED = [
    ['daily-expenses', '/dashboard/expenses?tab=daily'],
    ['shoot-expenses', '/dashboard/expenses?tab=shoot'],
    ['other-expenses', '/dashboard/expenses?tab=other'],
    ['salary-credited', '/dashboard/run-payroll'],
    ['payroll-register', '/dashboard/run-payroll?tab=register'],
    ['salary-register', '/dashboard/run-payroll?tab=register'],
    ['approve-payroll', '/dashboard/payslips'],
    ['advances', '/dashboard/pay-adjustments?tab=advances'],
    ['overtime', '/dashboard/pay-adjustments?tab=overtime'],
    ['payroll-incentives', '/dashboard/pay-adjustments?tab=incentives'],
    ['increments', '/dashboard/pay-adjustments?tab=increments'],
    ['salary-structures', '/dashboard/payroll-setup?tab=structures'],
    ['compliance', '/dashboard/payroll-setup?tab=statutory'],
    ['bank-details', '/dashboard/payroll-setup?tab=structures'],
    ['attendance-overview', '/dashboard/attendance-leave?tab=overview'],
    ['attendance', '/dashboard/attendance-leave?tab=attendance'],
    ['attendance-reports', '/dashboard/attendance-leave?tab=reports'],
    ['leave-policy/setup', '/dashboard/leave-policy?tab=setup'],
    ['leave-policy/types', '/dashboard/leave-policy?tab=types'],
    ['leave-policy/calendar', '/dashboard/leave-policy?tab=calendar'],
    ['leave-policy/shifts', '/dashboard/leave-policy?tab=shifts'],
    ['financial-year', '/dashboard/settings?tab=financial-year'],
    ['org/entities', '/dashboard/entities'],
    ['org/departments', '/dashboard/organisation?tab=departments'],
    ['org/designations', '/dashboard/organisation?tab=designations'],
    ['missed-punches', '/dashboard/my-activity?tab=punches'],
    ['site-visits', '/dashboard/client-locations?tab=visits'],
    ['location-tracking', '/dashboard/client-locations?tab=routes'],
];

export default function App() {
    return (
        <Routes>
            <Route path="/login" element={<PublicOnlyRoute><LoginPage /></PublicOnlyRoute>} />

            <Route path="/careers/:careersName" element={<CareersHome />} />
            <Route path="/careers/:careersName/track" element={<CareersTrack />} />
            <Route path="/careers/:careersName/:publicKey" element={<CareersJob />} />

            <Route element={<ProtectedRoute />}>
                <Route path="/dashboard" element={<DashboardLayout />}>
                    <Route index element={<DashboardHome />} />

                    <Route element={<ModuleGuard />}>
                    <Route element={<MasterAdminRoute />}>
                        <Route path="master" element={<MasterDashboardPage />} />
                        <Route path="chat/settings" element={<ChatSettingsPage />} />
                    </Route>

                    <Route path="inbox" element={<InboxPage />} />
                    <Route path="chat" element={<ChatPage />} />
                    <Route path="employees" element={<EmployeesPage />} />
                    <Route path="employees/onboard" element={<OnboardEmployeePage />} />
                    <Route path="employees/bulk-import" element={<BulkImportEmployeesPage />} />
                    <Route path="employees/:id" element={<EmployeeDetailPage />} />
                    <Route path="resignations" element={<ResignationsPage />} />
                    <Route path="recruitment" element={<RecruitmentPage />} />
                    <Route path="recruitment/candidates/:id" element={<CandidateDetailPage />} />
                    <Route path="documents" element={<DocumentsPage />} />

                    <Route path="run-payroll" element={<RunPayrollPage />} />
                    <Route path="payslips" element={<PayslipsPage />} />
                    <Route path="pay-adjustments" element={<PayAdjustmentsPage />} />

                    <Route path="expenses" element={<ExpensesPage />} />

                    <Route path="attendance-leave" element={<AttendanceLeavePage />} />
                    <Route path="leave-management" element={<LeaveManagementPage />} />
                    <Route path="attendance-history" element={<AttendanceHistoryPage />} />
                    <Route path="my-activity" element={<MyActivityPage />} />
                    <Route path="my-documents" element={<MyDocumentsPage />} />                    <Route path="client-locations" element={<FieldOperationsPage />} />

                    <Route path="entities" element={<BusinessEntitiesPage />} />
                    <Route path="organisation" element={<OrganisationPage />} />
                    <Route path="payroll-setup" element={<PayrollSetupPage />} />
                    <Route path="leave-policy" element={<LeavePolicyPage />} />
                    <Route path="roles" element={<RolesPage />} />
                    <Route path="roles/:roleId/users" element={<RoleUsersPage />} />
                    <Route path="roles/:roleId/access" element={<RoleAccessPage />} />

                    <Route path="settings" element={<SettingsPage />} />

                    {MOVED.map(([from, to]) => (
                        <Route key={from} path={from} element={<Navigate to={to} replace />} />
                    ))}
                    </Route>
                </Route>
            </Route>

            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
    );
}
